import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import type { BreadcrumbItem } from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const breadcrumbItems: BreadcrumbItem[] = [
    { name: '首页', path: '/' },
    { name: '关于我们' },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    try {
      const formData = new FormData(form);
      if (honeypotEl) {
        formData.delete('phone_alt');
      }

      const response = await fetch('https://readdy.ai/api/form/d9oscbgsenbnioleskug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后再试';
        if (typeof serverMsg === 'string' && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
          setFormStatus('success');
          return;
        }
        setFormStatus('error');
        setFormError(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '布丁漫画网',
    url: 'https://budingmanhua.com',
    description: '布丁漫画网是专业的中文漫画在线阅读平台，提供海量漫画资源免费阅读。',
    foundingDate: '2023',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'support@budingmanhua.com',
      availableLanguage: ['Chinese'],
    },
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于布丁漫画网 - 优质中文漫画阅读平台',
    description: '布丁漫画网品牌介绍、用户口碑评价、联系方式。了解布丁漫画网的发展历程和优质服务。',
    url: 'https://budingmanhua.com/about',
  };

  const advantages = [
    { icon: 'ri-shield-check-line', title: '正版授权', desc: '与多家漫画平台合作，提供正版授权内容，尊重创作者版权。' },
    { icon: 'ri-flashlight-line', title: '极速加载', desc: '采用先进CDN加速技术，确保漫画页面秒开，下拉式阅读流畅无卡顿。' },
    { icon: 'ri-smartphone-line', title: '多端适配', desc: '完美适配电脑、平板和手机，随时随地畅享漫画世界。' },
    { icon: 'ri-emotion-happy-line', title: '优质体验', desc: '无广告干扰、无删减内容，提供纯净的沉浸式阅读体验。' },
  ];

  const testimonials = [
    {
      name: '漫画迷小李',
      avatar: 'https://readdy.ai/api/search-image?query=A%20simple%20cute%20anime%20style%20avatar%20of%20a%20young%20student%20with%20glasses%2C%20warm%20friendly%20expression%2C%20clean%20illustration%20on%20cream%20background&width=120&height=120&seq=avatar-01&orientation=squarish',
      text: '布丁漫画网的阅读体验真的太棒了！下拉式阅读让我可以一口气追完十几话，不用频繁点击翻页，沉浸感超强。而且画质清晰，加载速度也很快。',
      rating: 5,
    },
    {
      name: '追漫达人阿杰',
      avatar: 'https://readdy.ai/api/search-image?query=A%20simple%20anime%20style%20avatar%20of%20a%20cool%20young%20man%20with%20headphones%2C%20casual%20expression%2C%20clean%20illustration%20on%20cream%20background&width=120&height=120&seq=avatar-02&orientation=squarish',
      text: '我每天都在布丁漫画网追更，更新速度很快，基本和日本同步。洞洞杂货铺是我最喜欢的漫画，每周末都在等更新，完全停不下来！',
      rating: 5,
    },
    {
      name: '漫画家小林',
      avatar: 'https://readdy.ai/api/search-image?query=A%20simple%20anime%20style%20avatar%20of%20a%20gentle%20female%20artist%20with%20a%20beret%2C%20warm%20smile%2C%20clean%20illustration%20on%20cream%20background&width=120&height=120&seq=avatar-03&orientation=squarish',
      text: '作为一个漫画创作者，我很认可布丁漫画网对版权的重视。平台的读者群体也很活跃，评论区氛围很好，是对创作者最好的鼓励。',
      rating: 4,
    },
    {
      name: '学生党小陈',
      avatar: 'https://readdy.ai/api/search-image?query=A%20simple%20anime%20style%20avatar%20of%20a%20cheerful%20teenage%20student%20with%20backpack%2C%20bright%20smile%2C%20clean%20illustration%20on%20cream%20background&width=120&height=120&seq=avatar-04&orientation=squarish',
      text: '免费的漫画资源太良心了！对于学生党来说真的超级友好。而且分类很全，不管是热血、恋爱还是悬疑都能找到喜欢的作品。',
      rating: 5,
    },
  ];

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于布丁漫画网 - 优质中文漫画在线阅读平台"
        description="布丁漫画网品牌介绍、用户口碑评价、联系方式。了解布丁漫画网的发展历程、核心优势和优质服务。海量漫画免费阅读，每日更新。"
        keywords="布丁漫画网,关于我们,品牌介绍,漫画平台,联系我们,用户口碑,在线漫画"
        canonicalUrl="https://budingmanhua.com/about"
        schemaJsonLd={[orgSchema, aboutSchema]}
      />
      <Navbar />
      <main>
        <div className="pt-24 pb-14 md:pb-20">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            <section id="brand" className="mt-6">
              <div className="text-center max-w-2xl mx-auto">
                <h1 className="font-heading text-3xl md:text-4xl text-foreground-950">
                  关于布丁漫画网
                </h1>
                <p className="mt-4 text-foreground-600 leading-relaxed">
                  布丁漫画网成立于2023年，是一家专注于提供优质中文漫画在线阅读体验的平台。我们致力于为漫画爱好者打造一个纯净、流畅、内容丰富的漫画阅读空间。无论是洞洞杂货铺这样的热门连载，还是冷门小众佳作，您都能在这里找到属于自己的阅读乐趣。
                </p>
              </div>

              <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                {advantages.map((adv) => (
                  <div key={adv.title} className="p-6 bg-background-100 rounded-xl text-center">
                    <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-accent-100 text-accent-700">
                      <i className={`${adv.icon} text-xl`} />
                    </div>
                    <h3 className="mt-4 font-heading text-base text-foreground-800">
                      {adv.title}
                    </h3>
                    <p className="mt-2 text-sm text-foreground-500 leading-relaxed">
                      {adv.desc}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 items-center">
                <div>
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20warm%20cozy%20illustration%20of%20a%20person%20reading%20manga%20on%20a%20tablet%20in%20a%20comfortable%20chair%20near%20a%20window%2C%20soft%20natural%20light%2C%20peaceful%20atmosphere%2C%20artistic%20style%2C%20cream%20and%20warm%20amber%20tones%2C%20no%20text&width=800&height=600&seq=about-reading-05&orientation=landscape"
                    alt="布丁漫画网 - 沉浸式阅读体验"
                    title="布丁漫画网阅读体验"
                    className="w-full rounded-xl"
                  />
                </div>
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">
                    我们的使命
                  </h2>
                  <p className="mt-4 text-foreground-600 leading-relaxed">
                    布丁漫画网的使命是让每一个漫画爱好者都能享受到最优质的阅读体验。我们采用下拉式连续阅读模式，让您无需频繁翻页，沉浸在漫画的精彩世界中。
                  </p>
                  <p className="mt-3 text-foreground-600 leading-relaxed">
                    我们与多家漫画创作平台建立了合作关系，确保内容的合法性和多样性。同时，我们不断优化技术架构，使用先进的CDN加速和图片压缩技术，保证每一位读者都能获得流畅的加载体验。
                  </p>
                  <div className="mt-6 grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-background-100 rounded-xl">
                      <span className="block text-2xl font-bold text-primary-500">120+</span>
                      <span className="text-xs text-foreground-500">漫画作品</span>
                    </div>
                    <div className="text-center p-4 bg-background-100 rounded-xl">
                      <span className="block text-2xl font-bold text-primary-500">50万+</span>
                      <span className="text-xs text-foreground-500">活跃读者</span>
                    </div>
                    <div className="text-center p-4 bg-background-100 rounded-xl">
                      <span className="block text-2xl font-bold text-primary-500">99.8%</span>
                      <span className="text-xs text-foreground-500">好评率</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section id="reputation" className="mt-16 md:mt-20">
              <div className="text-center max-w-2xl mx-auto">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">
                  用户口碑
                </h2>
                <p className="mt-2 text-sm text-foreground-500">
                  来自真实用户的评价，是我们前进的最大动力
                </p>
              </div>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
                {testimonials.map((t) => (
                  <div key={t.name} className="p-5 md:p-6 bg-background-100 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                        <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-foreground-800">{t.name}</h4>
                        <div className="flex items-center gap-0.5 mt-0.5">
                          {Array.from({ length: 5 }, (_, i) => (
                            <i
                              key={i}
                              className={`ri-star-fill text-xs ${i < t.rating ? 'text-accent-500' : 'text-foreground-200'}`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                      &ldquo;{t.text}&rdquo;
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section id="contact" className="mt-16 md:mt-20">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">
                    联系我们
                  </h2>
                  <p className="mt-2 text-sm text-foreground-500">
                    有任何问题或建议，欢迎随时联系我们
                  </p>
                  <div className="mt-6 space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-mail-line text-lg" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-foreground-800">电子邮箱</h4>
                        <p className="text-sm text-foreground-500">support@budingmanhua.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 flex-shrink-0">
                        <i className="ri-wechat-line text-lg" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-foreground-800">微信公众号</h4>
                        <p className="text-sm text-foreground-500">布丁漫画网</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-700 flex-shrink-0">
                        <i className="ri-weibo-line text-lg" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-foreground-800">官方微博</h4>
                        <p className="text-sm text-foreground-500">@布丁漫画网</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-200 text-foreground-600 flex-shrink-0">
                        <i className="ri-time-line text-lg" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-foreground-800">工作时间</h4>
                        <p className="text-sm text-foreground-500">周一至周五 9:00 - 18:00</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="bg-background-100 rounded-xl p-6 md:p-8">
                    <h3 className="font-heading text-xl text-foreground-800 mb-5">发送消息</h3>
                    <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          您的姓名 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          id="contact-name"
                          type="text"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          电子邮箱 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          id="contact-email"
                          type="email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          主题
                        </label>
                        <input
                          id="contact-subject"
                          type="text"
                          name="subject"
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入消息主题"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          消息内容 <span className="text-primary-500">*</span>
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 transition-colors resize-none"
                          placeholder="请输入您的消息内容（最多500字）"
                        />
                      </div>

                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="antispam-field"
                      />

                      <button
                        type="submit"
                        className="w-full py-3 bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        发送消息
                      </button>

                      {formStatus === 'success' && (
                        <div className="p-3 bg-accent-100 text-accent-800 text-sm rounded-lg">
                          <i className="ri-check-line mr-1" />
                          消息已发送成功！我们会尽快回复您。
                        </div>
                      )}
                      {formStatus === 'error' && (
                        <div className="p-3 bg-primary-100 text-primary-700 text-sm rounded-lg">
                          <i className="ri-error-warning-line mr-1" />
                          {formError || '提交失败，请稍后再试'}
                        </div>
                      )}
                    </form>
                  </div>
                </div>
              </div>
            </section>

            <section id="privacy" className="mt-16 md:mt-20">
              <div className="bg-background-100 rounded-xl p-6 md:p-8">
                <h2 className="font-heading text-xl md:text-2xl text-foreground-950">
                  隐私政策
                </h2>
                <div className="mt-4 space-y-3 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    布丁漫画网（以下简称"我们"）深知个人信息对您的重要性，我们将按照法律法规的要求，采取相应的安全保护措施，尽力保护您的个人信息安全。
                  </p>
                  <p>
                    我们仅收集为您提供漫画阅读服务所必需的最少信息，包括但不限于：您在使用过程中产生的浏览记录、收藏记录等。我们不会向任何第三方出售或分享您的个人信息。
                  </p>
                  <p>
                    我们使用行业标准的安全技术来保护您的个人信息，防止未经授权的访问、使用或泄露。您有权随时查阅、更正或删除您的个人信息。
                  </p>
                  <p>
                    如您对本隐私政策有任何疑问，请通过我们的官方联系方式与我们取得联系。
                  </p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}